import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import resolveImageUrl from "@/lib/imageUrl";
import { Impact as LegacyImpact } from "./StaticPages";
import { usePageSEO, SEO } from "@/lib/seo";

// Public /impact route.
// - Reads /api/impact. If state != "live" it falls back to the legacy
//   `Impact` component so production keeps rendering the current page.
// - `?preview=1` calls the authenticated admin endpoint (ignores state).

export default function ImpactPageRoute() {
  usePageSEO(SEO.impact.title, SEO.impact.description);
  const { lang } = useI18n();
  const T = (obj) => (obj && (obj[lang] || obj.en || obj.da)) || "";

  const [payload, setPayload] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setErr(null);
    const previewMode = new URLSearchParams(window.location.search).get("preview") === "1";
    try {
      if (previewMode) {
        try {
          const r = await api.get("/admin/impact/preview", { withCredentials: true, timeout: 12000 });
          setPayload({ ...r.data, enabled: true });
          return;
        } catch { /* fall through to public */ }
      }
      const r = await api.get("/impact-page", { timeout: 12000 });
      setPayload(r.data);
    } catch (e) {
      setErr(String(e?.message || e));
      // Fall back to legacy on hard failure so /impact never stays stuck.
      setPayload({ enabled: false, state: "draft", sections: [] });
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  if (err && !payload) {
    return (
      <div className="container-x max-editorial py-24 text-center">
        <div className="label-mono text-terracotta text-[10px]">/impact</div>
        <h1 className="mt-3 font-editorial text-3xl">Something went wrong loading this page.</h1>
        <button onClick={load} data-testid="impact-retry"
                className="mt-6 inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-6 py-3 text-sm tracking-wide">
          Try again
        </button>
      </div>
    );
  }
  if (!payload) {
    return <div className="container-x max-editorial py-24 text-ink/40 text-sm" data-testid="impact-loading">Loading…</div>;
  }

  const isLive = payload.enabled && (payload.sections || []).length > 0;
  if (!isLive) return <LegacyImpact />;

  return (
    <div className="bg-base text-ink" data-testid="impact-hub">
      {payload.sections.map(s => (
        <SectionRenderer key={s.id} s={s.data} T={T} />
      ))}
    </div>
  );
}

// External-safe CTA: absolute URLs render as <a target=_blank>, everything
// else uses React Router. Empty URL renders no link at all.
const CTA = ({ to, className, children, ...rest }) => {
  if (!to) return null;
  const external = /^https?:\/\//i.test(to);
  if (external) {
    return <a href={to} target="_blank" rel="noopener noreferrer" className={className} {...rest}>{children}</a>;
  }
  return <Link to={to} className={className} {...rest}>{children}</Link>;
};

function SectionRenderer({ s, T }) {
  switch (s.template) {
    case "hero":                return <Hero s={s} T={T} />;
    case "stats_grid":          return <StatsGrid s={s} T={T} />;
    case "partner_cards":       return <PartnerCards s={s} T={T} />;
    case "social_run_feature":  return <SocialRunFeature s={s} T={T} />;
    case "cause_card":          return <CauseCard s={s} T={T} />;
    case "steps_three":         return <StepsThree s={s} T={T} />;
    case "editorial_text":      return <EditorialText s={s} T={T} />;
    case "final_cta":           return <FinalCTA s={s} T={T} />;
    default:                    return <EditorialText s={s} T={T} />;
  }
}

const focalPos = (im) => `${im?.focal?.x ?? 50}% ${im?.focal?.y ?? 50}%`;

function Body({ text, className = "text-ink/80" }) {
  if (!text) return null;
  return text.split("\n\n").map((p, i) => (
    <p key={i} className={`mt-4 text-[15px] md:text-[17px] leading-relaxed whitespace-pre-line ${className}`}>{p}</p>
  ));
}

// ─── HERO ─────────────────────────────────────────────────────────────
function Hero({ s, T }) {
  const img = s.images?.[0] || {};
  const showHeadline = s.show_headline !== false;
  return (
    <section className="relative min-h-[78vh] flex items-end overflow-hidden bg-ink" data-testid="impact-hero">
      {img.url && (
        <img src={resolveImageUrl(img.url)} alt={T(img.alt) || ""} loading="eager"
             className="absolute inset-0 w-full h-full object-cover"
             style={{ objectPosition: focalPos(img) }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/55 to-ink/20" />
      <div className="relative max-editorial container-x pt-32 pb-16 md:pb-24 text-white">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]">{T(s.eyebrow)}</div>
        {showHeadline && (
          <h1 className="mt-4 font-editorial text-[40px] md:text-[80px] leading-[0.98] tracking-tighter max-w-4xl">{T(s.headline)}</h1>
        )}
        <div className="mt-6 max-w-2xl text-white/85"><Body text={T(s.body)} className="text-white/85" /></div>
      </div>
    </section>
  );
}

// ─── STATS GRID ───────────────────────────────────────────────────────
function StatsGrid({ s, T }) {
  const stats = s.stats || [];
  return (
    <section className="border-t border-line" data-testid="impact-stats">
      <div className="max-editorial container-x py-20 md:py-28">
        {(T(s.eyebrow) || T(s.headline)) && (
          <div className="max-w-2xl mb-10 md:mb-14">
            {T(s.eyebrow) && <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>}
            {T(s.headline) && <h2 className="mt-3 font-editorial text-[28px] md:text-[44px] leading-[1.02] tracking-tighter">{T(s.headline)}</h2>}
          </div>
        )}
        <dl className="grid md:grid-cols-4 gap-8 md:gap-4">
          {stats.map((st, i) => (
            <div key={i} className="border-t border-ink/20 pt-5 pr-4" data-testid={`impact-stat-${i}`}>
              <dt className="font-editorial text-3xl md:text-[40px] leading-[1] tracking-tight">{st.value}</dt>
              <dd className="mt-2 label-mono text-ink/60 tracking-widest text-[10px]">{T(st.label)}</dd>
              {T(st.note) && <div className="mt-3 text-[11px] text-ink/55 leading-snug italic">{T(st.note)}</div>}
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

// ─── PARTNER CARDS (Valencia 2024) ────────────────────────────────────
function PartnerCards({ s, T }) {
  const img = s.images?.[0];
  const partners = s.partners || [];
  return (
    <section className="border-t border-line" data-testid="impact-partners">
      {img?.url && (
        <img src={resolveImageUrl(img.url)} alt={T(img.alt) || ""} loading="lazy"
             className="w-full aspect-[4/3] md:aspect-[16/9] object-cover"
             style={{ objectPosition: focalPos(img) }} />
      )}
      <div className="max-editorial container-x py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10 md:gap-14 mb-16">
          <div className="md:col-span-4">
            <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
            <h2 className="mt-3 font-editorial text-[32px] md:text-[52px] leading-[1.02] tracking-tighter">{T(s.headline)}</h2>
          </div>
          <div className="md:col-span-7 md:col-start-6"><Body text={T(s.body)} /></div>
        </div>
        <div className="grid md:grid-cols-2 gap-8 md:gap-10">
          {partners.map((p, i) => (
            <PartnerCard key={i} p={p} T={T} idx={i} />
          ))}
        </div>
        {T(s.cta_label) && s.cta_url && (
          <div className="mt-14">
            <CTA to={s.cta_url}
                 data-testid="impact-partners-cta"
                 className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-8 py-4 text-sm tracking-wide">
              {T(s.cta_label)} <ArrowRight size={14} />
            </CTA>
          </div>
        )}
      </div>
    </section>
  );
}

function PartnerCard({ p, T, idx }) {
  const nameEl = <h3 className="mt-6 font-editorial text-2xl md:text-3xl leading-[1.15] tracking-tight">{p.name}</h3>;
  const nameLinked = p.name_link !== false && p.url;
  const logoLinked = p.logo_link !== false && p.url;
  return (
    <article data-testid={`impact-partner-${idx}`}
             className="border border-line hover:border-ink transition-colors p-8 md:p-12 flex flex-col">
      <div className="flex items-center justify-between">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">
          {T({en: "HUMANITARIAN PARTNER", da: "HUMANITÆR PARTNER", es: "SOCIO HUMANITARIO"})}
        </div>
        {p.logo_url && (
          logoLinked ? (
            <a href={p.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
              <img src={resolveImageUrl(p.logo_url)} alt={p.name} className="max-h-10 opacity-90 hover:opacity-100" />
            </a>
          ) : (
            <img src={resolveImageUrl(p.logo_url)} alt={p.name} className="max-h-10 opacity-90" />
          )
        )}
      </div>
      <div aria-hidden className="mt-4 h-px w-16 bg-terracotta" />
      {nameLinked ? (
        <a href={p.url} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta">{nameEl}</a>
      ) : nameEl}
      <p className="mt-5 text-ink/75 text-[15px] md:text-base leading-relaxed">{T(p.description)}</p>
      {p.url && T(p.cta_label) && (
        <div className="mt-8 md:mt-auto md:pt-8">
          <a href={p.url} target="_blank" rel="noopener noreferrer"
             data-testid={`impact-partner-cta-${idx}`}
             className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-6 py-3 text-sm tracking-wide">
            {T(p.cta_label)} →
          </a>
        </div>
      )}
    </article>
  );
}

// ─── SOCIAL RUN FEATURE ───────────────────────────────────────────────
function SocialRunFeature({ s, T }) {
  const img = s.images?.[0];
  const stats = s.stats || [];
  return (
    <section className="bg-ink text-white border-t border-line" data-testid="impact-social-run">
      <div className="max-editorial container-x py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-14">
        <div className="md:col-span-5">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
          <h2 className="mt-4 font-editorial text-[32px] md:text-[52px] leading-[1] tracking-tighter">{T(s.headline)}</h2>
          <div className="mt-6"><Body text={T(s.body)} className="text-white/80" /></div>
          {stats.length > 0 && (
            <dl className="mt-10 grid grid-cols-3 gap-4 md:gap-6 border-t border-white/15 pt-8">
              {stats.map((st, i) => (
                <div key={i} className="border-r border-white/15 last:border-r-0 pr-3">
                  <dt className="font-editorial text-xl md:text-2xl leading-tight">{st.value}</dt>
                  <dd className="label-mono text-white/60 tracking-widest text-[10px] mt-2">{T(st.label)}</dd>
                </div>
              ))}
            </dl>
          )}
          {T(s.cta_label) && s.cta_url && (
            <CTA to={s.cta_url}
                 data-testid="impact-social-run-cta"
                 className="mt-10 inline-flex items-center gap-2 text-sm tracking-[0.16em] uppercase border-b border-white/40 pb-1 hover:border-terracotta hover:text-terracotta transition-colors">
              {T(s.cta_label)} <ArrowRight size={14} />
            </CTA>
          )}
        </div>
        <div className="md:col-span-7 md:col-start-6">
          {img?.url ? (
            <div className="aspect-[16/10] overflow-hidden">
              <img src={resolveImageUrl(img.url)} alt={T(img.alt) || ""} loading="lazy"
                   style={{ objectPosition: focalPos(img) }}
                   className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-[900ms]" />
            </div>
          ) : (
            <div className="aspect-[16/10] overflow-hidden">
              <img src={resolveImageUrl("/images/paella.jpg")} alt=""
                   loading="lazy" className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── CAUSE CARD (RENACE Ávila) ────────────────────────────────────────
function CauseCard({ s, T }) {
  const cause = s.cause || {};
  const nameLinked = cause.name_link !== false && cause.url;
  const logoLinked = cause.logo_link !== false && cause.url;
  const nameEl = <h3 className="font-editorial text-3xl md:text-4xl leading-[1.1] tracking-tight">{cause.name}</h3>;
  return (
    <section className="border-t border-line" data-testid="impact-cause">
      <div className="max-editorial container-x py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-14">
        <div className="md:col-span-4">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
          <h2 className="mt-3 font-editorial text-[32px] md:text-[52px] leading-[1.02] tracking-tighter">{T(s.headline)}</h2>
        </div>
        <div className="md:col-span-7 md:col-start-6">
          <div className="border border-line p-8 md:p-12">
            <div className="flex items-start justify-between gap-4">
              {nameLinked ? (
                <a href={cause.url} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta">{nameEl}</a>
              ) : nameEl}
              {cause.logo_url && (
                logoLinked ? (
                  <a href={cause.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                    <img src={resolveImageUrl(cause.logo_url)} alt={cause.name} className="max-h-12" />
                  </a>
                ) : (
                  <img src={resolveImageUrl(cause.logo_url)} alt={cause.name} className="max-h-12" />
                )
              )}
            </div>
            <p className="mt-3 text-sm text-ink/60">{T(cause.description)}</p>
            {cause.promoted_by && (
              <p className="mt-4 label-mono text-[10px] tracking-widest text-ink/50">
                {T({en: "PROMOTED BY", da: "PROMOVERET AF", es: "IMPULSADA POR"})} · {cause.promoted_by}
              </p>
            )}
            <div className="mt-6"><Body text={T(s.body)} /></div>
            {T(s.status_label) && (
              <div className="mt-6 border-t border-line/70 pt-5 text-sm italic text-ink/70">
                {T(s.status_label)}
              </div>
            )}
            {s.show_final_amount && s.final_amount && (
              <div className="mt-3 text-sm text-ink/70">
                <span className="label-mono text-[10px] tracking-widest text-ink/50">
                  {T({en: "FINAL DONATION", da: "ENDELIG DONATION", es: "DONACIÓN FINAL"})}
                </span>{" "}
                <span className="font-editorial text-lg">{s.final_amount}</span>
              </div>
            )}
            {s.show_transfer_date && s.transfer_date && (
              <div className="mt-1 text-sm text-ink/60">
                <span className="label-mono text-[10px] tracking-widest text-ink/50">
                  {T({en: "TRANSFER DATE", da: "OVERFØRSELSDATO", es: "FECHA DE TRANSFERENCIA"})}
                </span> {s.transfer_date}
              </div>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              {T(cause.cta_label) && cause.url && (
                <a href={cause.url} target="_blank" rel="noopener noreferrer"
                   data-testid="impact-cause-cta"
                   className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white px-6 py-3 text-sm tracking-wide">
                  {T(cause.cta_label)} <ArrowRight size={14} />
                </a>
              )}
              {T(cause.related_event_label) && cause.related_event_url && (
                <CTA to={cause.related_event_url}
                     className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-6 py-3 text-sm tracking-wide">
                  {T(cause.related_event_label)}
                </CTA>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── STEPS ────────────────────────────────────────────────────────────
function StepsThree({ s, T }) {
  const steps = s.steps || [];
  return (
    <section className="bg-off-white border-t border-line" style={{ backgroundColor: "#F6F1EA" }} data-testid="impact-steps">
      <div className="max-editorial container-x py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
          <h2 className="mt-3 font-editorial text-[28px] md:text-[44px] leading-[1.02] tracking-tighter">{T(s.headline)}</h2>
          {T(s.body) && <div className="mt-4"><Body text={T(s.body)} /></div>}
        </div>
        <ol className="mt-12 md:mt-16 grid md:grid-cols-3 gap-8 md:gap-10">
          {steps.map((st, i) => (
            <li key={i} className="border-t border-ink/25 pt-5" data-testid={`impact-step-${i}`}>
              <div className="label-mono text-terracotta tracking-widest text-[10px]">{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-2 font-editorial text-xl md:text-2xl leading-tight">{T(st.name)}</div>
              {T(st.text) && <p className="mt-3 text-sm md:text-base text-ink/70 leading-relaxed">{T(st.text)}</p>}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ─── EDITORIAL TEXT (Transparency) ────────────────────────────────────
function EditorialText({ s, T }) {
  return (
    <section className="border-t border-line" data-testid={`impact-editorial-${s.slug}`}>
      <div className="max-editorial container-x py-20 md:py-28 max-w-3xl">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
        <h2 className="mt-4 font-editorial text-[28px] md:text-[44px] leading-[1.02] tracking-tighter">{T(s.headline)}</h2>
        <div className="mt-6"><Body text={T(s.body)} /></div>
        {T(s.cta_label) && s.cta_url && (
          <CTA to={s.cta_url}
               data-testid={`impact-editorial-cta-${s.slug}`}
               className="mt-8 inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-6 py-3 text-sm tracking-wide">
            {T(s.cta_label)} <ArrowRight size={14} />
          </CTA>
        )}
      </div>
    </section>
  );
}

// ─── FINAL CTA ────────────────────────────────────────────────────────
function FinalCTA({ s, T }) {
  return (
    <section className="bg-terracotta text-white" data-testid="impact-final">
      <div className="max-editorial container-x py-24 md:py-32">
        <div className="max-w-3xl">
          {T(s.eyebrow) && <div className="label-mono text-white/70 tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>}
          <h2 className="mt-4 font-editorial text-[36px] md:text-[64px] leading-[1] tracking-tighter">{T(s.headline)}</h2>
          {T(s.body) && <p className="mt-6 text-white/90 text-lg md:text-xl leading-relaxed">{T(s.body)}</p>}
          <div className="mt-10 flex flex-wrap gap-3">
            {T(s.cta_label) && s.cta_url && (
              <CTA to={s.cta_url}
                   data-testid="impact-final-cta1"
                   className="inline-flex items-center gap-2 bg-white text-terracotta hover:bg-ink hover:text-white px-8 py-4 text-sm tracking-wide">
                {T(s.cta_label)} <ArrowRight size={14} />
              </CTA>
            )}
            {T(s.cta2_label) && s.cta2_url && (
              <CTA to={s.cta2_url}
                   data-testid="impact-final-cta2"
                   className="inline-flex items-center gap-2 border border-white/70 text-white hover:bg-white hover:text-terracotta px-8 py-4 text-sm tracking-wide">
                {T(s.cta2_label)}
              </CTA>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
